import React from 'react';

interface CheckboxOption {
  value: string;
  label: string;
  description?: string;
}

interface CheckboxGroupProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: readonly CheckboxOption[] | CheckboxOption[];
  error?: boolean;
  className?: string;
  columns?: 1 | 2 | 3;
}

export function CheckboxGroup({
  values,
  onChange,
  options,
  error = false,
  className = '',
  columns = 2,
}: CheckboxGroupProps) {
  // Defensive: ensure values is always an array
  const safeValues = values || [];

  const handleToggle = (value: string) => {
    const newValues = safeValues.includes(value)
      ? safeValues.filter((v) => v !== value)
      : [...safeValues, value];
    onChange(newValues);
  };

  const gridClass = `grid gap-3 ${
    columns === 1 ? 'grid-cols-1' : columns === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-2 md:grid-cols-3'
  }`;

  return (
    <div
      className={`${gridClass} p-4 rounded-lg border ${
        error ? 'border-red-500' : 'border-slate-200'
      } bg-slate-50 ${className}`}
    >
      {options.map((option) => (
        <label
          key={option.value}
          className={`flex items-start gap-2.5 text-sm cursor-pointer p-2 rounded transition duration-150 ${
            safeValues.includes(option.value) 
              ? 'text-[#1D314D] bg-[#CEAB65]/10' 
              : 'text-slate-600 hover:text-[#1D314D] hover:bg-white'
          }`}
        >
          <input
            type="checkbox"
            value={option.value}
            checked={safeValues.includes(option.value)}
            onChange={() => handleToggle(option.value)}
            className="h-4 w-4 mt-0.5 text-[#CEAB65] rounded border-slate-300 focus:ring-[#CEAB65]"
          />
          <span className="flex-1">
            {option.label}
            {option.description && (
              <span className="block text-xs text-slate-500 mt-1">{option.description}</span>
            )}
          </span>
        </label>
      ))}
    </div>
  );
}
